'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk';
import reporter from '@bridge/utils/sentry';
import { LaunchDarklyDisabledContext } from './launchdarkly-disabled-context';
import { parseE2EOverrides } from './parseE2EOverrides';
import type { LDContext } from 'launchdarkly-react-client-sdk';
interface LaunchDarklyProviderProps {
  clientSideID: string;
  /** Flags evaluated before identify runs are only correct when the initial context already identifies the user. */
  context?: LDContext;
  /**
   * Raw e2e override value (a JSON flag map from the test harness); parsed here and used to
   * bootstrap the client so flags resolve synchronously without LaunchDarkly. Empty in prod.
   */
  e2eOverrides?: string;
  children: React.ReactNode;
}

/**
 * Inner component that monitors LaunchDarkly initialization and handles errors.
 * Captures initialization failures to Sentry for monitoring.
 */
function LaunchDarklyMonitor(t0) {
  const $ = _c(5);
  const {
    children
  } = t0;
  const ldClient = useLDClient();
  const hasLoggedRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== ldClient) {
    t1 = () => {
      if (!ldClient) {
        return;
      }
      const handleError = error => {
        if (!hasLoggedRef.current) {
          hasLoggedRef.current = true;
          reporter.captureException(error, {
            tags: {
              component: "LaunchDarklyProvider",
              errorType: "initialization_failure"
            },
            extra: {
              errorMessage: error.message
            }
          });
        }
      };
      ldClient.on("error", handleError);
      ldClient.waitForInitialization(5).catch(error_0 => {
        handleError(error_0);
      });
      return () => {
        ldClient.off("error", handleError);
      };
    };
    t2 = [ldClient];
    $[0] = ldClient;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== children) {
    t3 = <>{children}</>;
    $[3] = children;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
const isDev = process.env.NODE_ENV === 'development';

/**
 * Wraps children with a LaunchDarkly client provider. Pass `clientSideID`
 * (typically `process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID`). Initialization
 * failures route through `@bridge/utils/sentry` — apps must call
 * `registerReporter(Sentry)` at startup (instrumentation-client.ts) for these
 * to reach Sentry.
 *
 * In development, an empty `clientSideID` skips initialization entirely so
 * engineers can run locally without LD wired up. `useFlags()` falls back to
 * the empty default context, and the dev override sheet still works because
 * `LaunchDarklyFlagsList` seeds rows from `FEATURE_FLAGS`.
 */
export function LaunchDarklyProvider(t0) {
  const $ = _c(13);
  const {
    clientSideID,
    context,
    e2eOverrides,
    children
  } = t0;
  if (isDev && !clientSideID) {
    let t1;
    if ($[0] !== children) {
      t1 = <LaunchDarklyDisabledContext value={true}>{children}</LaunchDarklyDisabledContext>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== e2eOverrides) {
    t1 = parseE2EOverrides(e2eOverrides);
    $[2] = e2eOverrides;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const bootstrap = t1;
  let t2;
  if ($[4] !== bootstrap) {
    t2 = bootstrap ? {
      bootstrap
    } : undefined;
    $[4] = bootstrap;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== children) {
    t3 = <LaunchDarklyMonitor>{children}</LaunchDarklyMonitor>;
    $[6] = children;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== clientSideID || $[9] !== context || $[10] !== t2 || $[11] !== t3) {
    t4 = <LDProvider clientSideID={clientSideID} context={context} options={t2} timeout={5}>{t3}</LDProvider>;
    $[8] = clientSideID;
    $[9] = context;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}